import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

import PropTypes from 'prop-types';
import Loader from 'react-loader-spinner';
import makeAnimated from 'react-select/animated';
import ReactSelect, { createFilter } from 'react-windowed-select';

import CheckIcon from 'apps/web_forms/assets/check-white.svg';
import useSelectStyles from 'apps/web_forms/hooks/useSelectStyles';
import { COLORS } from 'apps/web_forms/utils/constants';

import {
  StyledLoadingIndicator,
  StyledMultipleSelect,
  StyledMultipleSelectCheckbox,
} from './style';

const animatedComponents = makeAnimated();

const SelectMultiple = ({
  options,
  identifier,
  disabled,
  initialAnswer,
  formContext,
  loadingResource,
  isRequired,
  onChangeValue,
  useSelectCheckbox,
  isValid,
  hasResourceUpdateHighlight,
  removeResourceUpdateHighlight,
  name,
}) => {
  const { getValues } = formContext;
  const multipleSelectRef = useRef(null);
  const [reactSelectValue, setReactSelectValue] = useState([]);
  const memoizedReactSelectValue = useMemo(
    () => reactSelectValue,
    [reactSelectValue.length]
  );

  const [dirtyCheckboxes, setDirtyCheckboxes] = useState(false);

  const selectedOptions = useRef(
    initialAnswer
      ? new Set(initialAnswer.split(',').map((item) => item.trim()))
      : new Set()
  );

  const invalidCheckboxes = useMemo(
    () =>
      dirtyCheckboxes && selectedOptions.current.size === 0 && isRequired
        ? 'invalid'
        : '',
    [dirtyCheckboxes, selectedOptions.current.size, isRequired]
  );

  const onCheckboxInvalid = ({ target }) =>
    target.setCustomValidity('You must select an option to continue');

  const updateValue = () => {
    const newValue = Array.from(selectedOptions.current).join(',');

    onChangeValue(identifier, newValue);
  };

  useEffect(
    function selectInitialAnswer() {
      /** added virtualValue and initialValue for Recycler view */
      const virtualValue = getValues(identifier);
      const initialValue = initialAnswer || virtualValue

      if (!initialAnswer && !virtualValue) return;

      setTimeout(() => {
        const initialAnswers = initialValue
          .split(',')
          .map((item) => item.trim());

        if (multipleSelectRef.current) {
          const multipleSelectContianer = multipleSelectRef.current;

          const optionList = Array.from(
            multipleSelectContianer.getElementsByTagName('input')
          );

          optionList.forEach((option) => {
            if (
              initialAnswers.find(
                (initialOption) => initialOption === option.value
              )
            ) {
              // eslint-disable-next-line no-param-reassign
              option.checked = true;
              handleOptionCheck({
                target: {
                  checked: true,
                  value: option.value,
                },
              });
            }
          });

          updateValue();
        }

        if (useSelectCheckbox) {
          const initialOptions = options
            .filter(
              (option) =>
                initialAnswers.includes(option.value) ||
                initialAnswers.includes(option.identifier)
            )
            .map((option) => {
              return option.value == null && option.identifier != null
                ? { ...option, value: option.identifier }
                : option;
            });
          setReactSelectValue(initialOptions);
        }
      }, 0);
    },
    [loadingResource, initialAnswer, options]
  );

  useEffect(
    function cleanPreviousSelectedOptions() {
      if (selectedOptions.current.size) {
        selectedOptions.current.forEach((optionValue) => {
          if (!options.find(({ value }) => value === optionValue)) {
            selectedOptions.current.delete(optionValue);
            updateValue();
          }
        });
      }
    },
    [options]
  );

  const handleOptionCheck = useCallback(({ target }) => {
    if (target.checked) {
      selectedOptions.current.add(target.value);
    } else {
      selectedOptions.current.delete(target.value);
    }

    updateValue();

    if (target.setCustomValidity) target.setCustomValidity('');

    if (!dirtyCheckboxes) {
      setDirtyCheckboxes(true);
    }
  }, []);

  const checkboxControl = (id, value, text, style) => {
    return (
      <div key={id} className='option-container' style={style}>
        <input
          id={id}
          className={`checkbox ${invalidCheckboxes} ${
            disabled ? 'read-only' : ''
          }`}
          defaultChecked={selectedOptions.current.has(value ?? text)}
          value={value ?? text}
          type='checkbox'
          disabled={disabled}
          onChange={handleOptionCheck}
          onInvalid={onCheckboxInvalid}
        />
        <CheckIcon className='check-icon' />
        <label htmlFor={id} className='checkbox-label'>
          {text}
        </label>
      </div>
    );
  };

  const renderCheckboxList = () => {
    return options.map(
      ({ text: currentText, identifier: customIdentifier, value }, index) => {
        const currentValue = value ?? customIdentifier;
        return checkboxControl(
          `${currentValue}-${currentText}-${index}`,
          currentValue,
          currentText
        );
      }
    );
  };

  const handleChange = (e) => {
    const result = e.map((v) => v.value || v.identifier);
    selectedOptions.current = result;
    updateValue();
    setReactSelectValue(e);
  };

  const { errorStyles, customStyles } = useSelectStyles({
    isValid,
    hasResourceUpdateHighlight,
  });

  return (
    <>
      {loadingResource && (
        <StyledLoadingIndicator>
          <span>Loading options</span>
          <Loader
            type='Oval'
            color={COLORS.primary}
            height='1.5em'
            width={15}
          />
        </StyledLoadingIndicator>
      )}

      {useSelectCheckbox && !loadingResource && (
        <StyledMultipleSelectCheckbox>
          <ReactSelect
            inputId={name}
            aria-label={name}
            theme={(theme) => ({
              ...theme,
              borderRadius: 0,
              colors: {
                ...theme.colors,
                ...errorStyles,
              },
            })}
            value={memoizedReactSelectValue}
            onFocus={removeResourceUpdateHighlight}
            components={animatedComponents}
            styles={customStyles}
            placeholder='Please select an option'
            isClearable
            options={options}
            isDisabled={disabled}
            filterOption={createFilter({ ignoreAccents: false })}
            getOptionLabel={(option) => option.text}
            getOptionValue={(option) => option.value || option.identifier}
            isMulti
            onChange={(e) => handleChange(e)}
          />
        </StyledMultipleSelectCheckbox>
      )}

      {!useSelectCheckbox && !loadingResource && (
        <StyledMultipleSelect
          className={`${disabled ? 'readOnly' : ''}`}
          ref={multipleSelectRef}
        >
          {options.length > 0 && renderCheckboxList()}
        </StyledMultipleSelect>
      )}
    </>
  );
};

SelectMultiple.propTypes = {
  options: PropTypes.arrayOf(
    PropTypes.shape({
      text: PropTypes.string,
      value: PropTypes.string,
      identifier: PropTypes.string,
    })
  ).isRequired,
  identifier: PropTypes.string.isRequired,
  disabled: PropTypes.bool.isRequired,
  initialAnswer: PropTypes.string,
  loadingResource: PropTypes.bool.isRequired,
  isRequired: PropTypes.bool,
  onChangeValue: PropTypes.func.isRequired,
  useSelectCheckbox: PropTypes.bool,
  isValid: PropTypes.bool,
  hasResourceUpdateHighlight: PropTypes.bool,
  removeResourceUpdateHighlight: PropTypes.func,
  name: PropTypes.string,
};

SelectMultiple.defaultProps = {
  initialAnswer: '',
  useSelectCheckbox: false,
  isRequired: false,
  isValid: undefined,
  hasResourceUpdateHighlight: false,
  removeResourceUpdateHighlight: () => {},
  name: '',
};

export default SelectMultiple;
